import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Checkbox, Dropdown, Empty, Input, Modal, Pagination, Select, Space, Spin, Tooltip, message } from 'antd'
import type { MenuProps } from 'antd'
import {
  AppstoreOutlined,
  AudioOutlined,
  CloudOutlined,
  CloudUploadOutlined,
  CustomerServiceOutlined,
  DeleteOutlined,
  DeploymentUnitOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  IdcardOutlined,
  MessageOutlined,
  MoreOutlined,
  PauseCircleFilled,
  PlayCircleFilled,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import {
  batchDeleteAudioAssets,
  deleteAudioAsset,
  listAudioAssets,
  renameAudioAsset,
  type AudioAssetItem,
  type AudioAssetLibraryView,
  type AudioAssetSort,
  type AudioAssetSource,
  type AudioAssetSummary,
  type AudioAssetTeamBlock,
  type AudioPresentationType,
} from '../api'
import { useWorkspaceState } from './WorkspaceContext'
import {
  buildCreatorSelectOptions,
  creatorFilterAfterViewChange,
  sanitizeCreatorFilter,
} from './team/creatorFilterOptions'
import AudioWaveformPlaceholder from './AudioWaveformPlaceholder'
import AudioUploadModal from './AudioUploadModal'
import { uploadDefaultPresentationTypeFromTab } from './audioAssetUploadDefaults'
import AudioAssetPreviewModal from './AudioAssetPreviewModal'
import './audio-asset-center.css'

const PAGE_SIZE_OPTIONS = [20, 50, 100]

type TabKey = 'all' | Lowercase<AudioPresentationType>

const TEAM_VIEWS: AudioAssetLibraryView[] = ['mine', 'shared', 'all']

const TAB_ORDER: TabKey[] = [
  'all',
  'voice_template',
  'speech',
  'music',
  'sfx',
  'ambience',
  'recording',
  'reference_audio',
  'mixdown',
]

const TAB_ICON: Record<TabKey, React.ReactNode> = {
  all: <AppstoreOutlined />,
  voice_template: <IdcardOutlined />,
  speech: <MessageOutlined />,
  music: <CustomerServiceOutlined />,
  sfx: <ThunderboltOutlined />,
  ambience: <CloudOutlined />,
  recording: <AudioOutlined />,
  reference_audio: <FileTextOutlined />,
  mixdown: <DeploymentUnitOutlined />,
}

// Presentation types renamable via the generic `meta["title"]` field —
// mirrors the backend's own `_RENAMABLE_PRESENTATION_TYPES` allow-list
// exactly (see `audio_asset_library_service.rename_audio_asset`
// docstring for why VOICE_TEMPLATE/SPEECH are excluded on purpose).
const RENAMABLE_TYPES = new Set<AudioPresentationType>(['MUSIC', 'SFX', 'AMBIENCE', 'RECORDING', 'REFERENCE_AUDIO', 'MIXDOWN'])

function formatDurationMs(ms: number | null): string {
  if (ms == null || Number.isNaN(ms) || ms <= 0) return '—'
  const totalSeconds = Math.round(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

/** "音频资产" — VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_
 * LIBRARY_P0_V1. A Unified Audio Asset View: reads exclusively from the
 * backend's already-aggregated `GET .../audio-assets` read-model (see
 * `audio_asset_library_service.py`'s own docstring) — this component
 * never merges/sorts/paginates multiple API calls itself (task spec
 * §65/§66/§67/§68: one list call = one page of already-final data +
 * an always-whole-workspace `summary` for the tabs).
 *
 * Default Table/List view only (task spec §9 — no grid/cover-image mode
 * exists for audio at all, unlike Image/Video Asset Center's grid+list
 * toggle) — no cover image logic anywhere in this file (task spec §8/§81).
 *
 * Exactly one `<audio>` element backs playback for the entire table (the
 * hidden element below, `audioElRef`) — clicking a second row's Play
 * always pauses/rebinds that same element (task spec §49/§50/§51: one
 * playing item at a time, no autoplay, no per-row `<audio>` proliferation
 * that could each independently start loading bytes).
 */
export default function AudioAssetsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(['audioAssets', 'actions', 'imageAssets'])
  const { workspace } = useWorkspaceState()
  const workspaceId = workspace?.id || ''

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AudioAssetItem[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<AudioAssetSummary | null>(null)
  const [team, setTeam] = useState<AudioAssetTeamBlock | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [teamView, setTeamView] = useState<AudioAssetLibraryView>('mine')
  const teamEnabled = Boolean(team?.enabled)
  const teamEnabledRef = useRef(false)

  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [q, setQ] = useState('')
  const [sourceFilter, setSourceFilter] = useState<AudioAssetSource | 'all'>('all')
  // Team Workspaces only — mirrors Image Asset Center creator filter (created_by_user_id).
  const [creatorFilter, setCreatorFilter] = useState<string>('all')
  const [sort, setSort] = useState<AudioAssetSort>('recent')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const [uploadOpen, setUploadOpen] = useState(false)
  const uploadDefaultPresentationType = useMemo(
    () => uploadDefaultPresentationTypeFromTab(activeTab),
    [activeTab],
  )
  const [previewItem, setPreviewItem] = useState<AudioAssetItem | null>(null)
  const [renameTarget, setRenameTarget] = useState<AudioAssetItem | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)

  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioElRef = useRef<HTMLAudioElement>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setLoadError(null)
    try {
      const { data } = await listAudioAssets(workspaceId, {
        q: q.trim() || undefined,
        type: activeTab === 'all' ? undefined : (activeTab.toUpperCase() as AudioPresentationType),
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        sort,
        page,
        page_size: pageSize,
        view: teamView,
        // Only send after the server has confirmed team mode (same as Image Asset Center).
        ...(teamEnabledRef.current && creatorFilter !== 'all' ? { creator_id: creatorFilter } : {}),
      })
      setItems(data.items)
      setTotal(data.total)
      setSummary(data.summary)
      const nextTeam = data.team ?? { enabled: false, active_member_count: 0 }
      teamEnabledRef.current = Boolean(nextTeam.enabled)
      setTeam(nextTeam)
      setCreatorFilter((prev) => sanitizeCreatorFilter(prev, nextTeam.creator_options))
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev
        const keep = new Set(data.items.map((i) => i.id))
        const next = new Set([...prev].filter((id) => keep.has(id)))
        return next.size === prev.size ? prev : next
      })
    } catch (e) {
      setLoadError((e as Error).message || t('audioAssets:toast.loadFailed'))
      message.error((e as Error).message || t('audioAssets:toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, q, activeTab, sourceFilter, creatorFilter, sort, page, pageSize, teamView, t])

  useEffect(() => {
    setTeamView('mine')
    setTeam(null)
    setCreatorFilter('all')
    teamEnabledRef.current = false
  }, [workspaceId])

  useEffect(() => {
    void load()
  }, [load])

  const creatorLabel = useCallback(
    (item: AudioAssetItem) => {
      if (!item.creator) return t('imageAssets:team.creator.unknown')
      return item.creator.is_me ? t('imageAssets:team.creator.me') : item.creator.display_name
    },
    [t],
  )

  const shareLabel = useCallback(
    (item: AudioAssetItem) =>
      item.visibility === 'private' ? t('imageAssets:team.share.private') : t('imageAssets:team.share.shared'),
    [t],
  )

  // Keep an already-open preview drawer in sync with the list it was
  // opened from (same convention as `VideoAssetsPage`'s own effect).
  useEffect(() => {
    if (!previewItem) return
    const fresh = items.find((i) => i.id === previewItem.id)
    if (fresh && fresh !== previewItem) setPreviewItem(fresh)
  }, [items, previewItem])

  const stopPlayback = useCallback(() => {
    audioElRef.current?.pause()
    setPlayingId(null)
  }, [])

  const togglePlay = useCallback(
    (item: AudioAssetItem) => {
      if (!item.playback_url) return
      const el = audioElRef.current
      if (!el) return
      if (playingId === item.id) {
        el.pause()
        setPlayingId(null)
        return
      }
      el.src = item.playback_url
      setPlayingId(item.id)
      void el.play().catch(() => {
        setPlayingId(null)
        message.error(t('audioAssets:detail.playbackError'))
      })
    },
    [playingId, t],
  )

  const onTabClick = (key: TabKey) => {
    setActiveTab(key)
    setPage(1)
    stopPlayback()
  }

  const doDelete = async (item: AudioAssetItem) => {
    try {
      await deleteAudioAsset(workspaceId, item.id)
      message.success(t('audioAssets:toast.deleted'))
      if (previewItem?.id === item.id) setPreviewItem(null)
      if (playingId === item.id) stopPlayback()
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
      void load()
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message
      message.error(msg || (e as Error).message || t('audioAssets:toast.deleteFailed'))
      throw e
    }
  }

  const openRename = (item: AudioAssetItem) => {
    setRenameTarget(item)
    setRenameValue(item.name)
  }

  const submitRename = async () => {
    if (!renameTarget) return
    const name = renameValue.trim()
    if (!name) return
    setRenaming(true)
    try {
      await renameAudioAsset(workspaceId, renameTarget.id, name)
      message.success(t('audioAssets:toast.renamed'))
      setRenameTarget(null)
      void load()
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message
      message.error(msg || (e as Error).message)
    } finally {
      setRenaming(false)
    }
  }

  const onDownload = (item: AudioAssetItem) => {
    if (!item.playback_url) return
    window.open(item.playback_url, '_blank', 'noopener')
  }

  const onViewSource = (item: AudioAssetItem) => {
    if (!item.project_id) return
    navigate(`/workspace/${workspaceId}/content-production/${item.project_id}/canvas`)
  }

  const allPageSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id))
  const somePageSelected = items.some((i) => selectedIds.has(i.id))
  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }
  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const item of items) {
        if (checked) next.add(item.id)
        else next.delete(item.id)
      }
      return next
    })
  }
  const selectedItems = useMemo(() => items.filter((i) => selectedIds.has(i.id)), [items, selectedIds])
  const deletableSelected = useMemo(() => selectedItems.filter((i) => i.can_delete), [selectedItems])
  const skippedSelected = useMemo(() => selectedItems.filter((i) => !i.can_delete), [selectedItems])

  const onBatchDelete = () => {
    if (selectedItems.length === 0) return
    if (deletableSelected.length === 0) {
      Modal.warning({
        title: t('audioAssets:deleteConfirm.noneDeletableTitle'),
        content: t('audioAssets:deleteConfirm.noneDeletableDescription', { count: skippedSelected.length }),
        okText: t('actions:confirm'),
      })
      return
    }
    Modal.confirm({
      title: t('audioAssets:deleteConfirm.batchTitle', { count: deletableSelected.length }),
      width: 520,
      centered: true,
      okText: t('actions:delete'),
      okType: 'danger',
      cancelText: t('actions:cancel'),
      content: (
        <div>
          <p>{t('audioAssets:deleteConfirm.batchIntro')}</p>
          {skippedSelected.length > 0 && (
            <p style={{ marginTop: 8, color: 'var(--text-secondary)' }}>
              {t('audioAssets:deleteConfirm.batchSkipped', {
                deletable: deletableSelected.length,
                skipped: skippedSelected.length,
              })}
            </p>
          )}
        </div>
      ),
      onOk: async () => {
        try {
          const { data } = await batchDeleteAudioAssets(workspaceId, deletableSelected.map((i) => i.id))
          setSelectedIds(new Set())
          void load()
          if (data.failed_count === 0) {
            message.success(t('audioAssets:toast.batchDeleted', { count: data.deleted_count }))
          } else if (data.deleted_count > 0) {
            message.warning(
              t('audioAssets:toast.batchDeletePartial', {
                deleted: data.deleted_count,
                failed: data.failed_count,
              }),
            )
          } else {
            message.error(t('audioAssets:toast.batchDeleteAllFailed'))
          }
        } catch (e) {
          message.error((e as Error).message || t('audioAssets:toast.deleteFailed'))
        }
      },
    })
  }

  const rowActionsMenu = (item: AudioAssetItem): MenuProps => {
    const menuItems: MenuProps['items'] = []
    if (item.playback_url) {
      menuItems.push({ key: 'download', icon: <DownloadOutlined />, label: t('audioAssets:actions.download') })
    }
    if (item.project_id) {
      menuItems.push({ key: 'view-source', icon: <FolderOpenOutlined />, label: t('audioAssets:actions.viewSource') })
    }
    if (RENAMABLE_TYPES.has(item.presentation_type)) {
      menuItems.push({ key: 'rename', icon: <EditOutlined />, label: t('audioAssets:actions.rename') })
    }
    if (item.can_delete) {
      menuItems.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
        label:
          item.ownership === 'PLATFORM_REFERENCE' ? t('audioAssets:actions.unfavorite') : t('audioAssets:actions.delete'),
      })
    }
    return {
      items: menuItems,
      onClick: ({ key }) => {
        if (key === 'download') onDownload(item)
        else if (key === 'view-source') onViewSource(item)
        else if (key === 'rename') openRename(item)
        else if (key === 'delete') {
          Modal.confirm({
            title: t('audioAssets:deleteConfirm.singleTitle'),
            content: t('audioAssets:deleteConfirm.singleDescription'),
            okText: t('actions:delete'),
            okType: 'danger',
            cancelText: t('actions:cancel'),
            onOk: () => doDelete(item),
          })
        }
      },
    }
  }

  if (!workspaceId) return null

  const hasAnyFilterActive =
    q.trim() !== '' ||
    sourceFilter !== 'all' ||
    (teamEnabled && creatorFilter !== 'all') ||
    activeTab !== 'all'

  return (
    <div className="aac-page" data-testid="audio-asset-center">
      <header className="aac-header">
        <div className="aac-heading">
          <h1 className="aac-title">{t('audioAssets:title')}</h1>
          <p className="aac-subtitle">{t('audioAssets:subtitle')}</p>
        </div>
        <div className="aac-actions">
          {activeTab === 'voice_template' && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => navigate(`/workspace/${workspaceId}/content-production`)}
              data-testid="aac-create-voice-template-btn"
            >
              {t('audioAssets:actions.createVoiceTemplate')}
            </Button>
          )}
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => setUploadOpen(true)}
            data-testid="aac-upload-btn"
          >
            {t('audioAssets:uploadButton')}
          </Button>
        </div>
      </header>

      {teamEnabled && (
        <div className="iac-view-tabs" role="tablist" aria-label={t('imageAssets:team.viewsAria')} data-testid="aac-view-tabs">
          {TEAM_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={teamView === v}
              className={`iac-view-tab${teamView === v ? ' is-active' : ''}`}
              onClick={() => {
                setTeamView(v)
                setCreatorFilter((prev) => creatorFilterAfterViewChange(v, prev))
                setPage(1)
              }}
              data-testid={`aac-view-tab-${v}`}
            >
              {t(`imageAssets:team.views.${v}`)}
            </button>
          ))}
        </div>
      )}

      <nav className="aac-tabs" aria-label={t('audioAssets:title')} data-testid="aac-tabs">
        {TAB_ORDER.map((key) => {
          const count = summary ? (key === 'all' ? summary.all : summary[key]) : undefined
          return (
            <button
              key={key}
              type="button"
              className={`aac-tab${activeTab === key ? ' is-active' : ''}`}
              onClick={() => onTabClick(key)}
              data-testid={`aac-tab-${key}`}
            >
              <span className="aac-tab-icon">{TAB_ICON[key]}</span>
              <span className="aac-tab-label">{t(`audioAssets:tabs.${key}`)}</span>
              <span className="aac-tab-count">{count ?? '—'}</span>
            </button>
          )
        })}
      </nav>

      <section className="aac-filters">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('audioAssets:filters.searchPlaceholder')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setPage(1)
          }}
          className="aac-search"
          data-testid="aac-search"
        />
        <Select
          value={sourceFilter}
          onChange={(v) => {
            setSourceFilter(v)
            setPage(1)
          }}
          className="aac-filter-select"
          options={[
            { value: 'all', label: t('audioAssets:filters.allSources') },
            { value: 'UPLOAD', label: t('audioAssets:sources.UPLOAD') },
            { value: 'AI_GENERATED', label: t('audioAssets:sources.AI_GENERATED') },
            { value: 'RECORDED', label: t('audioAssets:sources.RECORDED') },
            { value: 'PLATFORM_LIBRARY', label: t('audioAssets:sources.PLATFORM_LIBRARY') },
            { value: 'CANVAS', label: t('audioAssets:sources.CANVAS') },
            { value: 'VIDEO_PROJECT', label: t('audioAssets:sources.VIDEO_PROJECT') },
            { value: 'IMPORTED', label: t('audioAssets:sources.IMPORTED') },
          ]}
          data-testid="aac-source-filter"
        />
        {teamEnabled && teamView !== 'mine' && (
          <Select
            value={creatorFilter}
            onChange={(v) => {
              setCreatorFilter(v)
              setPage(1)
            }}
            aria-label={t('imageAssets:team.creator.filterAria')}
            className="aac-filter-select"
            data-testid="aac-creator-filter"
            options={buildCreatorSelectOptions(
              t('imageAssets:team.creator.label'),
              t('imageAssets:team.creator.all'),
              t('imageAssets:team.creator.me'),
              team?.creator_options,
              team?.user_id,
            )}
          />
        )}
        {/* Tag filter: no canonical cross-asset tag system exists yet
         * (task spec §35/§92 — see backend module docstring) — a real,
         * always-empty option list rather than a fabricated audio-only
         * tag set. */}
        <Tooltip title={t('audioAssets:filters.allTags')}>
          <Select
            value="all"
            disabled
            className="aac-filter-select"
            options={[{ value: 'all', label: t('audioAssets:filters.allTags') }]}
            data-testid="aac-tag-filter"
          />
        </Tooltip>
        <Select
          value={sort}
          onChange={(v) => {
            setSort(v)
            setPage(1)
          }}
          className="aac-filter-select"
          options={[
            { value: 'recent', label: t('audioAssets:filters.sortRecent') },
            { value: 'oldest', label: t('audioAssets:filters.sortOldest') },
            { value: 'name', label: t('audioAssets:filters.sortName') },
            { value: 'duration', label: t('audioAssets:filters.sortDuration') },
          ]}
          data-testid="aac-sort"
        />
        <Tooltip title={t('audioAssets:filters.refresh')}>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} aria-label={t('audioAssets:filters.refresh')} />
        </Tooltip>
      </section>

      {selectedItems.length > 0 && (
        <div className="aac-batch-bar" data-testid="aac-batch-bar">
          <span>{t('audioAssets:selection.count', { count: selectedItems.length })}</span>
          <Space>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={onBatchDelete}
              data-testid="aac-batch-delete"
            >
              {t('audioAssets:actions.delete')}
            </Button>
            <Button size="small" type="link" onClick={() => setSelectedIds(new Set())}>
              {t('actions:cancel')}
            </Button>
          </Space>
        </div>
      )}

      <Spin spinning={loading}>
        {loadError && !loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Empty description={loadError} />
            <Button style={{ marginTop: 12 }} onClick={() => void load()}>
              {t('actions:retry')}
            </Button>
          </div>
        ) : !loading && items.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Empty description={hasAnyFilterActive ? t('audioAssets:empty.noMatch') : t('audioAssets:empty.noAssets')} />
            {!hasAnyFilterActive && (
              <div className="aac-empty-actions">
                <Button icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>
                  {t('audioAssets:uploadButton')}
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(`/workspace/${workspaceId}/content-production`)}
                >
                  {t('audioAssets:actions.createVoiceTemplate')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="aac-table-scroll" data-testid="aac-list">
            <table className="aac-table">
              <thead>
                <tr>
                  <th className="aac-th-check">
                    <Checkbox
                      checked={allPageSelected}
                      indeterminate={!allPageSelected && somePageSelected}
                      onChange={(e) => toggleSelectAllPage(e.target.checked)}
                      aria-label={t('audioAssets:table.selectAllPage')}
                    />
                  </th>
                  <th className="aac-th-name">{t('audioAssets:table.name')}</th>
                  <th>{t('audioAssets:table.type')}</th>
                  <th>{t('audioAssets:table.duration')}</th>
                  <th>{t('audioAssets:table.source')}</th>
                  <th className="aac-th-secondary">{t('audioAssets:table.tags')}</th>
                  {teamEnabled && <th>{t('imageAssets:team.creator.label')}</th>}
                  {teamEnabled && <th>{t('imageAssets:team.share.column')}</th>}
                  <th>{t('audioAssets:table.createdAt')}</th>
                  <th className="aac-th-secondary">{t('audioAssets:table.usageCount')}</th>
                  <th className="aac-th-ops">{t('audioAssets:table.operations')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isVoiceTemplate = item.presentation_type === 'VOICE_TEMPLATE'
                  const isPlaying = playingId === item.id
                  return (
                    <tr
                      key={item.id}
                      className={selectedIds.has(item.id) ? 'is-selected' : undefined}
                      data-asset-id={item.id}
                      data-presentation-type={item.presentation_type}
                    >
                      <td className="aac-td-check">
                        <Checkbox checked={selectedIds.has(item.id)} onChange={(e) => toggleSelect(item.id, e.target.checked)} />
                      </td>
                      <td className="aac-td-name">
                        <button
                          type="button"
                          className="aac-name-cell"
                          onClick={() => setPreviewItem(item)}
                          data-testid="aac-row-open-detail"
                        >
                          <span
                            className={`aac-play-btn${item.playback_url ? '' : ' is-disabled'}`}
                            role="button"
                            aria-label={t('audioAssets:actions.use')}
                            onClick={(e) => {
                              e.stopPropagation()
                              togglePlay(item)
                            }}
                          >
                            {isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
                          </span>
                          <span className="aac-waveform">
                            <AudioWaveformPlaceholder
                              variant={item.has_preview_audio ? 'audio' : 'voiceTemplate'}
                              width={56}
                              height={20}
                            />
                          </span>
                          <span className="aac-name-text">
                            <span className="aac-name-primary" title={item.name}>
                              {item.name}
                            </span>
                            {item.description && <span className="aac-name-secondary">{item.description}</span>}
                          </span>
                        </button>
                      </td>
                      <td>
                        <span className={`aac-badge aac-badge-${item.presentation_type.toLowerCase()}`}>
                          {t(`audioAssets:tabs.${item.presentation_type.toLowerCase()}`)}
                        </span>
                        {item.status && (
                          <span className={`aac-status aac-status-${item.status.toLowerCase()}`}>
                            {t(`audioAssets:status.${item.status}`, item.status)}
                          </span>
                        )}
                      </td>
                      <td className="aac-td-mono">{isVoiceTemplate ? '—' : formatDurationMs(item.duration_ms)}</td>
                      <td>
                        <span className="aac-source-chip">{t(`audioAssets:sources.${item.source}`)}</span>
                        {item.ownership === 'PLATFORM_REFERENCE' && (
                          <span className="aac-badge aac-badge-platform">{t('audioAssets:badges.platformReference')}</span>
                        )}
                      </td>
                      <td className="aac-td-secondary aac-muted">
                        {item.tags.length > 0 ? item.tags.join(', ') : t('audioAssets:table.noTags')}
                      </td>
                      {teamEnabled && (
                        <td className="aac-td-secondary aac-muted" data-testid="aac-row-creator">
                          {creatorLabel(item)}
                        </td>
                      )}
                      {teamEnabled && (
                        <td data-testid="aac-row-share">
                          <span
                            className={`aac-share-badge${item.visibility === 'private' ? '' : ' is-workspace'}`}
                          >
                            {shareLabel(item)}
                          </span>
                        </td>
                      )}
                      <td className="aac-td-time">{formatAbsoluteTime(item.created_at)}</td>
                      <td className="aac-td-secondary aac-muted">{item.usage_count ?? t('audioAssets:table.noUsage')}</td>
                      <td className="aac-td-ops">
                        <Dropdown menu={rowActionsMenu(item)} trigger={['click']}>
                          <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            aria-label={t('audioAssets:actions.moreAria')}
                            data-testid="aac-row-more"
                          />
                        </Dropdown>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Spin>

      <footer className="aac-pagination">
        <span className="aac-total">{t('audioAssets:pagination.total', { count: total })}</span>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
          onChange={(p, ps) => {
            setPage(p)
            setPageSize(ps)
          }}
        />
      </footer>

      {/* Exactly one playback element for the entire table — task spec
       * §49/§50/§51 (single lightweight player, only one playing at a
       * time, no autoplay: this element has no `autoPlay` attribute and
       * only ever gets a `src`/`.play()` call from an explicit row-level
       * click). */}
      <audio
        ref={audioElRef}
        style={{ display: 'none' }}
        onEnded={() => setPlayingId(null)}
        onPause={() => {
          // A natural end/pause from outside `togglePlay` (e.g. the
          // element erroring) should not leave a stale "now playing" row.
        }}
        onError={() => {
          setPlayingId(null)
          message.error(t('audioAssets:detail.playbackError'))
        }}
        data-testid="aac-shared-audio-element"
      />

      <AudioUploadModal
        open={uploadOpen}
        workspaceId={workspaceId}
        defaultPresentationType={uploadDefaultPresentationType}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false)
          void load()
        }}
      />

      <AudioAssetPreviewModal item={previewItem} workspaceId={workspaceId} onClose={() => setPreviewItem(null)} onDelete={doDelete} />

      <Modal
        open={!!renameTarget}
        title={t('audioAssets:actions.rename')}
        onCancel={() => setRenameTarget(null)}
        onOk={() => void submitRename()}
        confirmLoading={renaming}
        okText={t('actions:save')}
        cancelText={t('actions:cancel')}
        destroyOnClose
      >
        <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} maxLength={200} autoFocus />
      </Modal>
    </div>
  )
}
